import { useState, useCallback, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import * as maplibregl from 'maplibre-gl'
import MainLayout from '@/components/Layout/MainLayout'
import { VisitorList } from '@/components/visitors/VisitorList'
import { VisitorMap } from '@/components/visitors/VisitorMap'
import { MobileVisitorMenu } from '@/components/visitors/MobileVisitorMenu'
import { VisitorDetailDrawer } from '@/components/inbox/VisitorDetailDrawer'
import { useStaleRefresh } from '@/hooks/useStaleRefresh'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { VisitorsPageProps, Visitor } from '@/components/visitors/types'

// Refresh visitors every 30 seconds while page is visible
const POLLING_INTERVAL = 30 * 1000

export default function Visitors({
  current_user,
  inbox,
  visitors,
  pmtiles_url,
  presence_stats,
}: VisitorsPageProps) {
  const { withInbox } = useRouteParams()

  // Refresh when returning to tab after being away
  useStaleRefresh({ staleThreshold: 2 * 60 * 1000 }) // 2 minutes

  // Poll for updates while page is visible
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        router.reload({ only: ['visitors', 'presence_stats'] })
      }
    }, POLLING_INTERVAL)

    return () => clearInterval(interval)
  }, [])
  const [selectedVisitorId, setSelectedVisitorId] = useState<string | null>(null)
  const [hoveredVisitorId, setHoveredVisitorId] = useState<string | null>(null)
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Get the selected visitor object
  const selectedVisitor = selectedVisitorId
    ? visitors.find(v => v.id === selectedVisitorId) ?? null
    : null

  // Click handler - select, zoom to visitor, and open drawer
  const handleVisitorSelect = useCallback(
    (visitor: Visitor) => {
      setSelectedVisitorId(visitor.id)
      setDrawerOpen(true)

      if (mapInstance && visitor.location) {
        mapInstance.flyTo({
          center: [visitor.location.longitude, visitor.location.latitude],
          zoom: 10,
          duration: 1500,
        })
      }
    },
    [mapInstance]
  )

  // Hover handler - center on visitor without changing zoom
  const handleVisitorHover = useCallback(
    (visitor: Visitor) => {
      setHoveredVisitorId(visitor.id)
      if (mapInstance && visitor.location) {
        mapInstance.easeTo({
          center: [visitor.location.longitude, visitor.location.latitude],
          duration: 500,
        })
      }
    },
    [mapInstance]
  )

  // Hover end handler - clear hover state
  const handleVisitorHoverEnd = useCallback(() => {
    setHoveredVisitorId(null)
  }, [])

  // Start conversation - navigate to new conversation page with visitor
  const handleStartConversation = useCallback(
    (visitor: Visitor) => {
      const basePath = routes.conversations.new.path(withInbox({ inbox_id: inbox.id }))
      router.visit(`${basePath}?visitor_id=${visitor.id}`)
    },
    [withInbox, inbox.id]
  )

  const handleMapReady = useCallback((map: maplibregl.Map) => {
    setMapInstance(map)
  }, [])

  // Debug: log zoom level when map zooms (dev only)
  useEffect(() => {
    if (!mapInstance || !import.meta.env.DEV) return

    const handleZoom = () => {
      console.log('Map zoom:', mapInstance.getZoom())
    }

    mapInstance.on('zoom', handleZoom)
    return () => {
      mapInstance.off('zoom', handleZoom)
    }
  }, [mapInstance])

  return (
    <>
      <Head title="Visitors" />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Visitors' }]}>
        <div className="flex flex-1 min-h-0 h-full relative">
          {/* Visitor List Sidebar */}
          <VisitorList
            visitors={visitors}
            selectedId={selectedVisitorId}
            onSelect={handleVisitorSelect}
            onHover={handleVisitorHover}
            onHoverEnd={handleVisitorHoverEnd}
            onStartConversation={handleStartConversation}
            presenceStats={presence_stats}
          />

          {/* Map Content */}
          <VisitorMap
            visitors={visitors}
            pmtilesUrl={pmtiles_url}
            selectedId={selectedVisitorId}
            hoveredId={hoveredVisitorId}
            onVisitorSelect={setSelectedVisitorId}
            onMapReady={handleMapReady}
            presenceStats={presence_stats}
          />

          {/* Mobile Visitor Menu */}
          <MobileVisitorMenu
            visitors={visitors}
            selectedId={selectedVisitorId}
            onSelect={handleVisitorSelect}
            onStartConversation={handleStartConversation}
            presenceStats={presence_stats}
          />

          {/* Visitor Detail Drawer */}
          {selectedVisitor && (
            <VisitorDetailDrawer
              visitor={selectedVisitor}
              open={drawerOpen}
              onOpenChange={setDrawerOpen}
            />
          )}
        </div>
      </MainLayout>
    </>
  )
}
